import { useState, useEffect } from 'react';
import { Calendar, Users, User, Shield } from 'lucide-react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import PaymentPage from '@/pages/PaymentPage';
import axios from 'axios';
import { API } from '@/App';

export default function MainApp({ auth }) {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkPaymentStatus();
    checkAdminStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payment/status`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to check payment status', error);
      // Fallback to avoid getting stuck on loading screen if API fails
      setPaymentStatus({ has_paid: true });
    }
  };

  const checkAdminStatus = () => {
    if (!auth.user) return;
    const adminEmails = ['federicson7@gmail.com', 'p.panayiotou2@gmail.com'];
    setIsAdmin(adminEmails.includes(auth.user.email.toLowerCase()));
  };

  // Show payment page if user hasn't paid
  if (paymentStatus && !paymentStatus.has_paid) {
    return <PaymentPage auth={auth} onPaymentComplete={checkPaymentStatus} />;
  }

  // Don't render until we know payment status
  if (!paymentStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/calendar' },
    { id: 'community', label: 'Community', icon: Users, path: '/community' },
    { id: 'profile', label: 'Profile', icon: User, path: '/profile' }
  ];

  if (isAdmin) {
    tabs.push({ id: 'admin', label: 'Admin', icon: Shield, path: '/admin' });
  }

  // Determine active tab based on current path
  const currentPath = location.pathname;
  const activeTab = tabs.find(tab => currentPath.startsWith(tab.path))?.id || 'calendar';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 pb-20" data-testid="main-app">
      {/* Main content */}
      <div className="max-w-4xl mx-auto">
        <Outlet />
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white bottom-nav" data-testid="bottom-navigation">
        <div className="max-w-4xl mx-auto flex justify-around items-center py-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`flex flex-col items-center gap-1 px-6 py-2 rounded-lg ${
                  isActive
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={24} />
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}